<!-- Loading: no "knxWeather" frame yet -->
<div *ngIf="waiting && !weather" class="row g-2 mb-3">
  <div class="col-6 col-md-4 col-xl-3" *ngFor="let _ of [0,1,2,3]">
    <div class="card">
      <div class="card-body p-2 p-sm-3">
        <app-skeleton width="60%" height=".75rem" radius="4px" [darkMode]="darkMode" style="display:block;margin-bottom:.45rem;"></app-skeleton>
        <app-skeleton width="45%" height="1.5rem" radius="4px" [darkMode]="darkMode" style="display:block;margin-bottom:.45rem;"></app-skeleton>
        <app-skeleton width="100%" height="56px" radius="4px" [darkMode]="darkMode" style="display:block;"></app-skeleton>
      </div>
    </div>
  </div>
</div>

<ng-container *ngIf="!(waiting && !weather)">

  <div class="dash-section-head">
    <div class="dash-section-title wx-title">
      <span *ngIf="cards.length" class="badge bg-danger dash-live"><span [style.opacity]="liveDotOn ? '1' : '0'">●</span> LIVE</span>
      <span class="dash-section-name">🌦 {{ 'KNX_WX_TITLE' | translate }}</span>
      <!-- station + age: beside the title, on a line of their own on phones -->
      <span *ngIf="station" class="dash-section-age wx-station">
        {{ station.device }}<ng-container *ngIf="station.line"> · {{ 'KNX_WX_LINE' | translate:{line: station.line} }}</ng-container><ng-container
          *ngIf="cards.length && weather?.updatedAt"> · {{ 'UPDATED_AGO' | translate:{time: updatedAgo(weather?.updatedAt ?? null)} }}</ng-container>
      </span>
    </div>
  </div>

  <!-- Empty state: no station known (yet) — or a known station (e.g. KNX_WEATHER_DEVICE) without values yet -->
  <div *ngIf="!cards.length" class="card mb-3">
    <div class="card-body p-3">
      <div class="fw-semibold small mb-1">{{ (station ? 'KNX_WX_NO_CHANNELS' : 'KNX_WX_NONE') | translate }}</div>
      <div class="text-muted" style="font-size:.75rem;">{{ (station ? 'KNX_WX_NO_CHANNELS_HINT' : 'KNX_WX_NONE_HINT') | translate }}</div>
    </div>
  </div>

  <!-- One card per channel: 2 per row on phones (a lone last one spans the width), 3 on tablets, 4 on large screens -->
  <div *ngIf="cards.length" class="row g-2 mb-3 m-fill-last">
    <div class="col-6 col-md-4 col-xl-3" *ngFor="let c of cards; trackBy: trackCard">
      <ng-template #wxTapTpl>
        <ng-container *ngTemplateOutlet="chartTpl; context:{c: c}"></ng-container>
      </ng-template>
      <div class="h-100 wx-flash" [class.section-flash]="flash[c.ga]">
        <app-popup-card popupMaxWidth="min(92vw, 1100px)" [tapTpl]="wxTapTpl" [linkId]="c.linkId" [boardChart]="'wx.' + c.series"
                        [popupTitle]="c.meta.icon + ' ' + (c.meta.label | translate)" (tappedChange)="onPopup(c, $event)">
          <ng-template #body>
            <div class="card-body p-2 p-sm-3 d-flex flex-column">
              <div class="wx-head d-flex align-items-start gap-1">
                <span class="wx-icon" aria-hidden="true">{{ c.meta.icon }}</span>
                <span class="wx-label" [attr.lang]="lang">{{ c.meta.label | translate }}</span>
                <span *ngIf="c.hintKey" class="wx-badge ms-auto d-none d-sm-inline-block" [class.wx-badge-unsure]="c.confidence === 'unsure'"
                      [title]="c.hintKey | translate">{{ (c.confidence === 'likely' ? 'KNX_WX_LIKELY' : 'KNX_WX_UNSURE') | translate }}</span>
              </div>
              <!-- phones: the confidence badge ends the GA line (the label keeps the card's width) -->
              <div class="wx-sub text-truncate m-wrap" [title]="c.ga + (c.name ? ' · ' + c.name : '') + (c.dpt ? ' · DPT ' + c.dpt : '')">
                {{ c.ga }}<ng-container *ngIf="c.name"> · {{ c.name }}</ng-container><ng-container *ngIf="!c.name && c.quantity === 'binary'"> · {{ 'KNX_WX_BINARY_HINT' | translate }}</ng-container>
                <span *ngIf="c.hintKey" class="wx-badge d-sm-none" [class.wx-badge-unsure]="c.confidence === 'unsure'"
                      [title]="c.hintKey | translate">{{ (c.confidence === 'likely' ? 'KNX_WX_LIKELY' : 'KNX_WX_UNSURE') | translate }}</span>
              </div>

              <!-- Numeric: big value + unit -->
              <div *ngIf="!c.binary" class="wx-value mt-1">
                <ng-container *ngIf="c.valueNum != null; else noValue">{{ c.valueNum }}<span *ngIf="c.valueUnit" class="wx-unit">{{ c.valueUnit }}</span></ng-container>
              </div>
              <!-- Binary: state pill + since when -->
              <div *ngIf="c.binary" class="d-flex align-items-center gap-2 mt-1 flex-wrap" style="min-height:1.95rem;">
                <span *ngIf="c.on != null; else noValue" class="wx-pill" [class.wx-pill-on]="c.on">{{ (c.on ? 'KNX_ON' : 'KNX_OFF') | translate }}</span>
                <ng-container *ngIf="footer(c) as f">
                  <span *ngIf="f.since" class="wx-since" [title]="f.sinceFull">{{ 'KNX_SINCE' | translate:{time: f.since} }}</span>
                  <span *ngIf="!f.since && c.tsText" class="wx-since">{{ 'KNX_WX_LAST_TELEGRAM' | translate:{time: c.tsText} }}</span>
                </ng-container>
              </div>
              <ng-template #noValue><span class="text-muted" style="font-size:.8rem;font-weight:400;">{{ 'KNX_WX_NO_VALUE' | translate }}</span></ng-template>

              <div class="mt-auto">
                <div class="wx-spark">
                  <canvas *ngIf="sparkData[c.series]" baseChart type="line"
                          [data]="sparkData[c.series]" [options]="sparkOptions[c.series]"></canvas>
                </div>
                <div class="wx-foot d-flex justify-content-between" *ngIf="footer(c) as f">
                  <ng-container *ngIf="!c.binary">
                    <span>{{ f.min }}</span><span>24 h</span><span>{{ f.max }}</span>
                  </ng-container>
                  <ng-container *ngIf="c.binary">
                    <span>{{ f.onPct != null ? ('KNX_WX_ON_SHARE' | translate:{pct: f.onPct}) : '' }}</span><span>24 h</span>
                  </ng-container>
                </div>
              </div>
            </div>
          </ng-template>
        </app-popup-card>
      </div>
    </div>
  </div>

</ng-container>

<!-- Expanded channel chart (tap / click on a card; deep link "#knx/weather/<series>", 🔗 in the popup header;
     the channel's name is the popup's title) -->
<ng-template #chartTpl let-c="c">
  <div class="d-flex align-items-center gap-2 mb-2 flex-wrap">
    <div class="wx-pop-info min-w-0">
      <span *ngIf="c.hintKey" class="wx-badge me-1" [class.wx-badge-unsure]="c.confidence === 'unsure'"
            [title]="c.hintKey | translate">{{ (c.confidence === 'likely' ? 'KNX_WX_LIKELY' : 'KNX_WX_UNSURE') | translate }}</span>
      <ng-container *ngIf="!c.binary && c.valueNum != null"><span class="fw-semibold" style="color:var(--bs-body-color);">{{ c.valueNum }} {{ c.valueUnit }}</span> · </ng-container>
      <ng-container *ngIf="c.binary && c.on != null"><span class="fw-semibold" style="color:var(--bs-body-color);">{{ (c.on ? 'KNX_ON' : 'KNX_OFF') | translate }}</span> · </ng-container>
      {{ c.ga }}<ng-container *ngIf="c.name"> · {{ c.name }}</ng-container><ng-container *ngIf="c.dpt"> · DPT {{ c.dpt }}</ng-container>
    </div>
    <!-- Range: a full-width segmented control on phones -->
    <div class="wx-pop-ctl d-flex align-items-center gap-2 ms-auto">
      <div class="btn-group btn-group-sm dash-seg" role="group">
        <button *ngFor="let r of chartRanges" type="button" class="btn"
                [ngClass]="range === r.key ? 'btn-secondary' : 'btn-outline-secondary'"
                [attr.aria-pressed]="range === r.key"
                (click)="setRange(r.key)">{{ r.label | translate }}</button>
      </div>
      <span *ngIf="expandedLoading" class="spinner-border spinner-border-sm text-muted" role="status"></span>
    </div>
  </div>
  <div class="wx-big">
    <canvas baseChart #wxChart="base-chart" type="line" [data]="expandedData" [options]="expandedOptions"></canvas>
    <div *ngIf="!expandedCount && !expandedLoading" class="wx-nodata text-muted small">{{ 'KEINE_DATEN' | translate }}</div>
    <button *ngIf="zoomed" type="button" class="btn btn-sm btn-outline-secondary dash-chart-reset"
            (click)="resetZoom(wxChart)">{{ 'CHART_RESET' | translate }}</button>
  </div>
  <div class="text-muted text-center mt-1 wx-pop-meta">
    {{ expandedCount }} {{ 'MESSPUNKTE' | translate }}
  </div>
</ng-template>
